"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** A single carousel card (stored in the section's `metadata` JSON). */
interface ProgramCards4Item {
  id: string;
  meta_thumbnail_image?: string;
  meta_thumbnail_image_alt?: string;
  meta_title?: string;
  meta_salary?: string;
  /** Comma-separated skills, rendered as chips. */
  meta_key_skills?: string;
  meta_priority?: number;
  meta_status?: boolean | number;
}

interface ProgramCards4Section {
  title?: string;
  title_text_color?: string;
  /** Color applied to the `{ }`-wrapped portion of the title. */
  title_highlight_color?: string;
  salary_text_color?: string;
  key_skills_text_color?: string;
  key_skills_background_color?: string;
  key_skills_border_color?: string;
  description?: string;
  note?: string;
  background_image?: string;
  background_image_alt?: string;
  metadata?: ProgramCards4Item[];
  status?: boolean | number;
}

interface ProgramCards4Props {
  data?: { data?: ProgramCards4Section } | ProgramCards4Section;
}

/** Live data uses `meta_status`; admin preview activates rows via `status` (see activateMetadata). */
const isItemVisible = (item: ProgramCards4Item & { status?: boolean }) =>
  Boolean(item?.meta_status) || Boolean(item?.status);

const splitSkills = (skills?: string): string[] =>
  (skills || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Mirrors `formatSectionTitle` but injects the highlight color inline so it is
 * CMS-configurable. Text wrapped in `{ }` (or `{{ }}` for a forced line break)
 * is colored with `highlightColor`.
 */
const formatTitleWithHighlight = (
  title: string,
  highlightColor?: string
): string => {
  if (!title) return "";
  const color = highlightColor || "#E1473F";
  const open = `<span style="color:${color}">`;
  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, `${open}$1</span><br/>`)
      .replace(/\s+/g, " ");
  }
  if (title.includes("{")) {
    return title
      .replace(/\{/g, open)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }
  return title;
};

const NavArrow: React.FC<{
  direction: "prev" | "next";
  disabled: boolean;
  accentColor: string;
  onClick: () => void;
}> = ({ direction, disabled, accentColor, onClick }) => (
  <button
    type="button"
    aria-label={direction === "prev" ? "Previous" : "Next"}
    onClick={onClick}
    disabled={disabled}
    className="flex size-[44px] items-center justify-center rounded-full transition-colors"
    style={{
      backgroundColor: disabled ? "#E2E2E2" : accentColor,
      cursor: disabled ? "default" : "pointer",
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      style={{ transform: direction === "prev" ? "rotate(180deg)" : undefined }}
    >
      <path
        d="M9 6l6 6-6 6"
        stroke={disabled ? "#000000" : "#FFFFFF"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

interface ProgramCards4CardProps extends ProgramCards4Item {
  salaryTextColor?: string;
  keySkillsTextColor?: string;
  keySkillsBackgroundColor?: string;
  keySkillsBorderColor?: string;
}

const ProgramCards4Card: React.FC<ProgramCards4CardProps> = ({
  salaryTextColor,
  keySkillsTextColor,
  keySkillsBackgroundColor,
  keySkillsBorderColor,
  ...item
}) => {
  const skills = splitSkills(item.meta_key_skills);
  return (
    <div className="snap-start shrink-0 rounded-bl-[16px] rounded-tr-[16px] w-[266px] bg-white shadow-md overflow-hidden">
      {item.meta_thumbnail_image && (
        <Image
          src={item.meta_thumbnail_image}
          alt={item.meta_thumbnail_image_alt || item.meta_title || ""}
          width={266}
          height={154}
          className="w-full h-[154px] border-[0.8px] border-black border-opacity-30 rounded-tr-[16px] object-cover"
        />
      )}

      <div className="p-3 min-h-[218px]">
        <div className="flex flex-col gap-2">
          <p className="text-heading-6 min-[768px]:leading-[126%]">{item.meta_title}</p>
          {item.meta_salary && (
            <div className="flex items-center justify-between">
              <p className="font-bold text-[12px] min-[768px]:leading-[126%]">
                Average Salary
              </p>
              <p
                className="font-extrabold text-[14px] min-[768px]:leading-[100%] text-primary"
                style={salaryTextColor ? { color: salaryTextColor } : undefined}
              >
                {item.meta_salary}
              </p>
            </div>
          )}
          {skills.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="font-bold text-[12px] min-[768px]:leading-[126%]">
                Key Skills
              </p>
              <div className="flex flex-wrap gap-2">
                {skills.map((skill, index) => (
                  <span
                    key={index}
                    className="rounded-full border bg-gray-100 px-3 py-1 text-[12px] leading-[126%]"
                    style={{
                      backgroundColor: keySkillsBackgroundColor || undefined,
                      color: keySkillsTextColor || undefined,
                      borderColor: keySkillsBorderColor || undefined,
                    }}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgramCards4: React.FC<ProgramCards4Props> = ({ data }) => {
  const raw = data as any;
  // Handle single/double nesting from the page + preview renderers.
  const section: ProgramCards4Section =
    raw?.data?.data ?? raw?.data ?? raw ?? {};

  const elementRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Target of an in-flight smooth scroll, so rapid clicks queue up. */
  const pendingScrollRef = useRef<number | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const metadata = useMemo(
    () =>
      (section.metadata || [])
        .filter(isItemVisible)
        .sort((a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0)),
    [section.metadata]
  );

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanPrev(scrollLeft > 1);
    setCanNext(scrollLeft + clientWidth < scrollWidth - 1);
    // Release the queued target once the smooth scroll has landed on it.
    if (
      pendingScrollRef.current !== null &&
      Math.abs(scrollLeft - pendingScrollRef.current) < 2
    ) {
      pendingScrollRef.current = null;
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => {
      window.removeEventListener("resize", updateScrollState);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
  }, [updateScrollState, metadata.length]);

  const scrollByCards = useCallback((direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const cards = Array.from(el.children) as HTMLElement[];
    if (cards.length === 0) return;

    // Measure card + gap from the DOM: the gap differs per breakpoint (24/34px),
    // so a hardcoded step drifts and leaves cards cut in half.
    const pitch =
      cards.length > 1
        ? cards[1].offsetLeft - cards[0].offsetLeft
        : cards[0].offsetWidth;
    if (pitch <= 0) return;

    // Anchor on the queued target rather than the live (mid-animation)
    // scrollLeft, so a double click advances two full cards instead of
    // compounding from a half-finished position.
    const current = pendingScrollRef.current ?? el.scrollLeft;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    const index = Math.min(
      Math.max(Math.round(current / pitch) + direction, 0),
      cards.length - 1
    );
    const target = Math.min(
      Math.max(cards[index].offsetLeft - cards[0].offsetLeft, 0),
      maxScroll
    );

    pendingScrollRef.current = target;
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    // Fallback release in case the scroll never reports the exact target
    // (interrupted by a manual swipe, reduced-motion instant jump, etc.).
    settleTimerRef.current = setTimeout(() => {
      pendingScrollRef.current = null;
    }, 800);
    el.scrollTo({ left: target, behavior: "smooth" });
  }, []);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (elementRef.current) {
  //     FireWidgetViewedEvent(elementRef, "careers_after_course");
  //   }
  // }, []);

  if (!section || !section.status || metadata.length === 0) return null;

  const showArrows = canPrev || canNext;
  // Shared accent for the note + carousel arrows.
  const accentColor = section.title_highlight_color || "#E1473F";

  return (
    <section
      ref={elementRef}
      className="w-full relative flex items-center justify-center overflow-hidden"
    >
      {/* Never give this a negative z-index: page wrappers (`main.bg-gray-1`)
          paint their background above `-z` descendants, which hides it on the
          preview/live pages. Keep it at auto and lift the content instead. */}
      {section.background_image && (
        <Image
          src={section.background_image}
          alt={section.background_image_alt || ""}
          fill
          sizes="100vw"
          className="object-cover object-center"
        />
      )}
      <div className="relative z-10 w-full min-[768px]:py-[42px] py-[50px] flex items-center justify-center min-[768px]:px-0 min-[1024px]:px-[100px]">
        <div className="flex flex-col gap-[24px] min-[768px]:gap-[42px] w-full min-[768px]:max-w-[862px]">
          <div className="flex flex-col px-[30px] w-full items-center justify-center gap-[10px]">
            <p
              className="text-black text-[28px] font-semibold min-[768px]:text-[32px] leading-[126%] max-w-[300px] min-[768px]:max-w-[570px] text-center"
              style={{ color: section.title_text_color, fontWeight: 600 }}
              dangerouslySetInnerHTML={{
                __html: formatTitleWithHighlight(
                  section.title ?? "",
                  section.title_highlight_color
                ),
              }}
            />
            {section.description && (
              <p className="max-w-[270px] text-center min-[768px]:max-w-full min-[768px]:text-[16px] text-[14px] font-normal leading-[22px]">
                {section.description}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-[8px]">
            {section.note && (
              <div className="flex items-center justify-center min-[768px]:items-start min-[768px]:justify-start w-full min-[768px]:w-fit text-center min-[768px]:ml-[60px]">
                <p
                  className="italic max-w-[290px] min-[768px]:max-w-full text-center min-[768px]:text-left text-heading-6 leading-[22px]"
                  style={{ color: accentColor, fontWeight: 600 }}
                >
                  {section.note}
                </p>
              </div>
            )}

            <div
              ref={scrollRef}
              onScroll={updateScrollState}
              className={cn(
                "flex gap-[24px] min-[768px]:gap-[34px] overflow-x-auto px-[30px] min-[768px]:px-0 pb-2",
                // Snap padding must match the track padding so a snapped card
                // keeps its left inset instead of clipping at the edge.
                "snap-x snap-mandatory scroll-pl-[30px] min-[768px]:scroll-pl-0",
                "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
                showArrows ? "justify-start" : "min-[768px]:justify-center"
              )}
            >
              {metadata.map((item) => (
                <ProgramCards4Card
                  key={item.id}
                  {...item}
                  salaryTextColor={section.salary_text_color}
                  keySkillsTextColor={section.key_skills_text_color}
                  keySkillsBackgroundColor={section.key_skills_background_color}
                  keySkillsBorderColor={section.key_skills_border_color}
                />
              ))}
            </div>

            {showArrows && (
              <div className="flex items-center justify-center gap-[10px] mt-[42px]">
                <NavArrow
                  direction="prev"
                  disabled={!canPrev}
                  accentColor={accentColor}
                  onClick={() => scrollByCards(-1)}
                />
                <NavArrow
                  direction="next"
                  disabled={!canNext}
                  accentColor={accentColor}
                  onClick={() => scrollByCards(1)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramCards4;
